
import { 
  Palette, Scissors, Sparkles, Users, Brain, Scan, Microscope, 
  Camera, Eye, Zap, Store, Calendar, ShoppingBag, CreditCard, 
  Building2, Star, Package, Truck, TrendingUp, Mail, MessageSquare, 
  Users2, Shield, FileText, Wrench, Activity 
} from 'lucide-react';
import { Product } from './types';

export const healthBeautyProducts: Product[] = [
  // Original Health & Beauty Products
  {
    id: 'cosmetics-ecommerce-platform',
    title: 'Cosmetics E-Commerce Platform',
    subtitle: 'Beauty retail solution',
    description: 'Specialized e-commerce platform for cosmetics with virtual try-on, product recommendations, inventory management, and customer analytics.',
    icon: Palette,
    image: 'https://images.unsplash.com/photo-1522335789203-aabd1fc54bc9?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Health & Beauty',
    status: 'Available',
    features: [
      'Virtual try-on technology',
      'Product catalog management',
      'Inventory tracking',
      'Customer analytics',
      'Review system',
      'Mobile optimization',
      'Payment integration',
      'Marketing tools'
    ],
    pricing: { setup: 38000, monthly: 1748 },
    color: 'from-pink-500 to-rose-600',
    businessTypes: ['#cosmetics', '#ecommerce', '#beauty', '#retail', '#virtual-try-on']
  },
  {
    id: 'complete-beauty-wellness-suite',
    title: 'Complete Beauty & Wellness Management Suite',
    subtitle: 'Comprehensive beauty business platform',
    description: 'All-in-one beauty and wellness management platform combining salon operations, spa services, wellness tracking, and advanced booking systems for complete business management.',
    icon: Sparkles,
    image: 'https://images.unsplash.com/photo-1544161515-4ab6ce6db874?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Health & Beauty',
    status: 'Available',
    features: [
      'Advanced online booking system',
      'Comprehensive staff scheduling',
      'Multi-service inventory management',
      'Integrated spa and salon operations',
      'Customer wellness tracking',
      'Advanced payment processing',
      'Beauty treatment scheduling',
      'Membership program management',
      'Automated marketing campaigns',
      'Advanced analytics and reporting',
      'Mobile app for clients and staff',
      'Wellness package management'
    ],
    pricing: { setup: 35000, monthly: 1610 },
    color: 'from-emerald-500 to-teal-600',
    businessTypes: ['#beauty-wellness', '#salon', '#spa', '#wellness-management', '#comprehensive-beauty']
  },
  {
    id: 'beauty-spa-wellness-management',
    title: 'Beauty Spa & Wellness Management System',
    subtitle: 'Holistic beauty wellness operations',
    description: 'Comprehensive beauty spa and wellness center management with treatment scheduling, therapist management, product inventory, and customer wellness tracking.',
    icon: Sparkles,
    image: 'https://images.unsplash.com/photo-1544161515-4ab6ce6db874?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Health & Beauty',
    status: 'Available',
    features: [
      'Beauty treatment scheduling',
      'Certified therapist management',
      'Beauty product inventory',
      'Customer wellness tracking',
      'Beauty package management',
      'Membership programs',
      'Payment processing',
      'Beauty marketing automation'
    ],
    pricing: { setup: 32000, monthly: 1472 },
    color: 'from-emerald-500 to-teal-600',
    businessTypes: ['#beauty-spa', '#wellness', '#beauty-treatments', '#holistic-beauty', '#health']
  },
  {
    id: 'personal-stylist-platform',
    title: 'Personal Stylist Platform',
    subtitle: 'Virtual styling services',
    description: 'AI-driven personal stylist platform with style recommendations, virtual wardrobe, outfit planning, and direct shopping links.',
    icon: Users,
    image: 'https://images.unsplash.com/photo-1445205170230-053b83016050?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Health & Beauty',
    status: 'Available',
    features: [
      'AI style recommendations',
      'Virtual wardrobe',
      'Outfit planning',
      'Direct shopping links',
      'Personalized style profiles',
      'Trend analysis',
      'Customer feedback',
      'Social sharing'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-purple-500 to-pink-600',
    businessTypes: ['#styling', '#fashion-ai', '#personalization', '#ecommerce', '#virtual-wardrobe']
  },

  // AI-Powered Consultation & Personalization (6 products)
  {
    id: 'haut-ai-skin-analysis',
    title: 'Haut.AI Skin Analysis Platform',
    subtitle: 'AI-powered skin diagnostics',
    description: 'Advanced AI platform offering virtual try-ons and hyper-personalized skincare recommendations using generative AI (SkinGPT) technology.',
    icon: Brain,
    image: 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Health & Beauty',
    status: 'Available',
    features: [
      'AI skin analysis and diagnostics',
      'Virtual try-on technology',
      'SkinGPT personalization engine',
      'Product recommendation system',
      'Skin condition tracking',
      'Before/after comparison tools',
      'Custom skincare regimens',
      'Professional consultation portal'
    ],
    pricing: { setup: 42000, monthly: 1932 },
    color: 'from-blue-500 to-purple-600',
    businessTypes: ['#ai-skincare', '#skin-analysis', '#personalization', '#diagnostics', '#beauty-tech']
  },
  {
    id: 'revieve-ai-platform',
    title: 'Revieve AI Beauty Platform',
    subtitle: 'Comprehensive AI beauty suite',
    description: 'All-in-one AI platform featuring AI Skincare Advisor, AI Makeup Artist, and personalized product recommendation engines for e-commerce.',
    icon: Scan,
    image: 'https://images.unsplash.com/photo-1596462502278-27bfdc403348?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Health & Beauty',
    status: 'Available',
    features: [
      'AI skincare advisor',
      'AI makeup artist tools',
      'Product recommendation engine',
      'Skin tone analysis',
      'Beauty consultation AI',
      'E-commerce integration',
      'Custom beauty profiles',
      'Multi-platform compatibility'
    ],
    pricing: { setup: 48000, monthly: 2208 },
    color: 'from-indigo-500 to-pink-600',
    businessTypes: ['#ai-beauty', '#makeup-ai', '#skincare-ai', '#beauty-consultation', '#ecommerce-ai']
  },
  {
    id: 'skin-dossier-diagnostics',
    title: 'Skin Dossier Multi-Diagnostic Platform',
    subtitle: 'Advanced skin analysis system',
    description: 'Multi-diagnostic platform combining AI, imaging, genomics, and health data to analyse skin and provide highly precise product matching for UK consumers.',
    icon: Microscope,
    image: 'https://images.unsplash.com/photo-1607619056574-7b8d3ee536b2?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Health & Beauty',
    status: 'Available',
    features: [
      'Multi-diagnostic skin analysis',
      'Genomic data integration',
      'Health data correlation',
      'Precision product matching',
      'Clinical-grade imaging',
      'Personalized treatment plans',
      'Long-term skin tracking',
      'Professional reporting tools'
    ],
    pricing: { setup: 52000, monthly: 2392 },
    color: 'from-teal-500 to-blue-600',
    businessTypes: ['#skin-diagnostics', '#genomics', '#precision-beauty', '#health-data', '#clinical-beauty']
  },
  {
    id: 'perfect-corp-youCam',
    title: 'Perfect Corp YouCam Platform',
    subtitle: 'AR virtual try-on solutions',
    description: 'Industry-leading AI and AR-powered virtual try-on solutions for makeup, hairstyles, and accessories for brand websites and mobile apps.',
    icon: Camera,
    image: 'https://images.unsplash.com/photo-1586281380117-5a60ae2050cc?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Health & Beauty',
    status: 'Available',
    features: [
      'AR makeup try-on',
      'Hairstyle simulation',
      'Accessory virtual fitting',
      'Real-time face tracking',
      'Brand website integration',
      'Mobile app SDK',
      'Social sharing features',
      'Analytics and insights'
    ],
    pricing: { setup: 45000, monthly: 2070 },
    color: 'from-purple-500 to-rose-600',
    businessTypes: ['#ar-beauty', '#virtual-try-on', '#makeup-ar', '#beauty-tech', '#mobile-beauty']
  },
  {
    id: 'voir-color-matching',
    title: 'Voir AI Color Matching',
    subtitle: 'Precision shade matching',
    description: 'Advanced AI color matching technology helping customers find their perfect foundation and concealer shades online with industry-leading accuracy.',
    icon: Eye,
    image: 'https://images.unsplash.com/photo-1512496015851-a90fb38ba796?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Health & Beauty',
    status: 'Available',
    features: [
      'AI color matching technology',
      'Foundation shade finder',
      'Concealer matching',
      'Skin tone analysis',
      'Lighting adjustment algorithms',
      'Multi-brand compatibility',
      'Mobile-optimized interface',
      'Return rate reduction tools'
    ],
    pricing: { setup: 35000, monthly: 1610 },
    color: 'from-amber-500 to-orange-600',
    businessTypes: ['#color-matching', '#foundation-finder', '#ai-beauty', '#shade-matching', '#beauty-ecommerce']
  },
  {
    id: 'terra-skincare-ai',
    title: 'Terra Personalized Skincare AI',
    subtitle: 'Custom skincare creation',
    description: 'AI-powered platform analyzing customer skin goals, concerns, and preferences to create fully personalized skincare regimens and products.',
    icon: Zap,
    image: 'https://images.unsplash.com/photo-1570554886111-e80fcca6a029?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Health & Beauty',
    status: 'Available',
    features: [
      'Personalized skincare regimens',
      'Skin goal analysis',
      'Concern-based recommendations',
      'Custom product formulation',
      'Progress tracking',
      'Ingredient optimization',
      'Routine scheduling',
      'Professional consultation integration'
    ],
    pricing: { setup: 38000, monthly: 1748 },
    color: 'from-green-500 to-teal-600',
    businessTypes: ['#personalized-skincare', '#ai-formulation', '#custom-beauty', '#skincare-ai', '#beauty-wellness']
  },

  // Business Management, POS & CRM (7 products)
  {
    id: 'mindbody-beauty-management',
    title: 'Mindbody Beauty Business Suite',
    subtitle: 'All-in-one beauty business platform',
    description: 'Comprehensive business management software for wellness and beauty businesses, handling booking, POS, CRM, marketing, and staff management.',
    icon: Store,
    image: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Health & Beauty',
    status: 'Available',
    features: [
      'Appointment booking system',
      'Point of sale integration',
      'Customer relationship management',
      'Staff scheduling and management',
      'Marketing automation',
      'Inventory management',
      'Payment processing',
      'Business analytics dashboard'
    ],
    pricing: { setup: 35000, monthly: 1610 },
    color: 'from-blue-500 to-indigo-600',
    businessTypes: ['#salon-management', '#spa-software', '#beauty-business', '#wellness-management', '#booking-system']
  },
  {
    id: 'booker-salon-platform',
    title: 'Booker by Mindbody Platform',
    subtitle: 'Popular salon management system',
    description: 'Leading platform for salons and spas to manage appointments, customer profiles, payments, and comprehensive marketing campaigns.',
    icon: Calendar,
    image: 'https://images.unsplash.com/photo-1521590832167-7bcbfaa6381f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Health & Beauty',
    status: 'Available',
    features: [
      'Advanced appointment scheduling',
      'Customer profile management',
      'Integrated payment processing',
      'Marketing campaign tools',
      'Staff performance tracking',
      'Inventory control system',
      'Mobile app for staff',
      'Customer loyalty programs'
    ],
    pricing: { setup: 32000, monthly: 1472 },
    color: 'from-rose-500 to-pink-600',
    businessTypes: ['#salon-software', '#appointment-booking', '#beauty-management', '#spa-platform', '#customer-management']
  },
  {
    id: 'vagaro-cloud-platform',
    title: 'Vagaro Cloud Management Platform',
    subtitle: 'Cloud-based beauty business solution',
    description: 'Comprehensive cloud-based salon, spa, and fitness management software offering online booking, POS, inventory management, and marketing tools.',
    icon: ShoppingBag,
    image: 'https://images.unsplash.com/photo-1487412947147-5cebf100ffc2?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Health & Beauty',
    status: 'Available',
    features: [
      'Cloud-based management',
      'Online booking system',
      'Integrated POS system',
      'Inventory tracking',
      'Marketing automation',
      'Customer communication tools',
      'Staff management portal',
      'Financial reporting'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-purple-500 to-blue-600',
    businessTypes: ['#cloud-salon', '#beauty-pos', '#spa-management', '#fitness-beauty', '#online-booking']
  },
  {
    id: 'zenoti-enterprise-platform',
    title: 'Zenoti Enterprise Beauty Platform',
    subtitle: 'Enterprise-level spa and salon solution',
    description: 'Enterprise-grade management software for spas and salons, providing complete solution for operations, customer experience, and business growth.',
    icon: Building2,
    image: 'https://images.unsplash.com/photo-1600334129128-685c5582fd35?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Health & Beauty',
    status: 'Available',
    features: [
      'Enterprise-grade operations',
      'Advanced customer experience tools',
      'Multi-location management',
      'Comprehensive reporting suite',
      'Staff productivity analytics',
      'Revenue optimization tools',
      'Custom workflow automation',
      'Integration ecosystem'
    ],
    pricing: { setup: 55000, monthly: 2530 },
    color: 'from-indigo-500 to-purple-600',
    businessTypes: ['#enterprise-beauty', '#spa-enterprise', '#multi-location', '#beauty-operations', '#salon-enterprise']
  },
  {
    id: 'meevo2-salon-platform',
    title: 'Meevo 2 Modern Salon Platform',
    subtitle: 'Next-generation salon management',
    description: 'Modern salon and spa management platform designed to streamline appointments, retail operations, customer communication, and comprehensive reporting.',
    icon: Star,
    image: 'https://images.unsplash.com/photo-1559599101-f09722fb4948?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Health & Beauty',
    status: 'Available',
    features: [
      'Modern appointment system',
      'Retail management tools',
      'Advanced customer communication',
      'Real-time reporting',
      'Staff performance metrics',
      'Automated marketing campaigns',
      'Mobile-first design',
      'Client history tracking'
    ],
    pricing: { setup: 33000, monthly: 1518 },
    color: 'from-teal-500 to-green-600',
    businessTypes: ['#modern-salon', '#appointment-management', '#retail-beauty', '#salon-communication', '#beauty-reporting']
  },
  {
    id: 'glossgenius-studio-platform',
    title: 'GlossGenius Studio Platform',
    subtitle: 'Beauty professional suite',
    description: 'Comprehensive platform built for beauty professionals and small studios, simplifying booking, payments, marketing, and website building in one solution.',
    icon: Users2,
    image: 'https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Health & Beauty',
    status: 'Available',
    features: [
      'Booking and scheduling',
      'Integrated payment processing',
      'Marketing automation',
      'Website builder for professionals',
      'Client management system',
      'Service menu customization',
      'Mobile app for professionals',
      'Financial tracking tools'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-pink-500 to-rose-600',
    businessTypes: ['#beauty-professional', '#studio-management', '#independent-beauty', '#beauty-booking', '#professional-tools']
  },

  // E-Commerce & Sales Enablement (4 products)
  {
    id: 'shopify-plus-beauty',
    title: 'Shopify Plus for Beauty Brands',
    subtitle: 'Enterprise beauty e-commerce',
    description: 'Enterprise-level e-commerce platform specifically optimized for beauty brands with scalability, extensive app ecosystem, and beauty-focused customizations.',
    icon: Package,
    image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Health & Beauty',
    status: 'Available',
    features: [
      'Enterprise-grade scalability',
      'Beauty-focused app ecosystem',
      'Custom checkout experiences',
      'Advanced inventory management',
      'Multi-channel selling',
      'International expansion tools',
      'Beauty-specific templates',
      'Advanced analytics suite'
    ],
    pricing: { setup: 45000, monthly: 2070 },
    color: 'from-green-500 to-emerald-600',
    businessTypes: ['#beauty-ecommerce', '#enterprise-beauty', '#scalable-beauty', '#beauty-retail', '#multi-channel']
  },
  {
    id: 'salesforce-commerce-beauty',
    title: 'Salesforce Commerce Cloud Beauty',
    subtitle: 'Omnichannel beauty commerce',
    description: 'Enterprise omnichannel commerce platform for major beauty brands with unified experiences across web, mobile, and physical stores.',
    icon: TrendingUp,
    image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    category: 'Health & Beauty',
    status: 'Available',
    features: [
      'Omnichannel customer experience',
      'Unified commerce platform',
      'Advanced personalization AI',
      'Mobile-first design',
      'Physical store integration',
      'Advanced CRM integration',
      'Global commerce capabilities',
      'Enterprise-grade security'
    ],
    pricing: { setup: 68000, monthly: 3128 },
    color: 'from-blue-500 to-indigo-600',
    businessTypes: ['#omnichannel-beauty', '#enterprise-commerce', '#personalized-beauty', '#global-beauty', '#ai-commerce']
  }
];
